import Swup from 'swup';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupScrollPlugin from '@swup/scroll-plugin';
// @ts-expect-error Package doesn't have types
import SwupGaPlugin from '@swup/ga-plugin';
// @ts-expect-error Package doesn't have types
import SwupGtmPlugin from '@swup/gtm-plugin';
import SwupScriptsPlugin from '@swup/scripts-plugin';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupProgressPlugin from '@swup/progress-plugin';
import { isFunction, type defineModule } from '../utils/helpers';
import { publishStickyHeaderHeight } from '../components/sticky-header';

// Modules
import aosModule from './aos';

const autoImportedModules = import.meta.glob<ReturnType<typeof defineModule>>(
  ['@/../Templates/blocks/*/script.ts', '../components/**/*.ts'],
  {
    eager: true,
    import: 'default',
  },
);

const modules: ReturnType<typeof defineModule>[] = [
  aosModule,
  ...Object.values(autoImportedModules),
];

const setup = () => Promise.all(modules.map((module) => module.setup()));
const cleanup = () =>
  Promise.all(
    modules.map((module) => isFunction(module.cleanup) && module.cleanup()),
  );

const swupNot = '[data-no-swup],[href*="/wp/"],[href*="/app/uploads/"]';

// The scroll plugin animates in JS and ignores `scroll-padding-top`, so anchors
// would land under a sticky header. Absolute lengths only — getComputedStyle
// leaves a percentage as-is, so `10%` would be read as 10px.
const getScrollOffset = () => {
  // `content:scroll` runs before the `visit:end` that re-runs the modules, and the
  // header was replaced along with `.page-wrapper` — so re-measure the incoming one
  // before reading, or the offset is the outgoing header's.
  publishStickyHeaderHeight();

  const value = getComputedStyle(document.documentElement).scrollPaddingTop;

  return Number.parseFloat(value) || 0;
};

// Getters, not plain values: the visitor can flip the OS setting without reloading.
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const animateScroll = {
  betweenPages: false,
  get samePage() {
    return !reducedMotion.matches;
  },
  get samePageWithHash() {
    return !reducedMotion.matches;
  },
};

export const swup = new Swup({
  animationSelector: '[class*="swup-transition-"]',
  containers: ['.page-wrapper'],
  plugins: [
    new SwupPreloadPlugin(),
    new SwupBodyClassPlugin(),
    new SwupScrollPlugin({
      animateScroll,
      offset: getScrollOffset,
    }),
    new SwupGaPlugin({ gaMeasurementId: window.gaTrackingId }),
    new SwupGtmPlugin(),
    new SwupScriptsPlugin({
      head: false,
      body: true,
    }),
    new SwupHeadPlugin({
      persistTags: import.meta.env.DEV ? 'style[data-vite-dev-id]' : false,
    }),
    new SwupProgressPlugin(),
  ],
  linkSelector: `a[href^="${window.location.origin}"]:not(${swupNot}), a[href^="/"]:not(${swupNot}), a[href^="#"]:not(${swupNot})`,
});

swup.hooks.on('visit:end', async () => {
  await setup();
});

swup.hooks.on('visit:start', async () => {
  await cleanup();
});

setup();
